import { UndefinedAble } from 'data/base';

/**
 * This file defies a common resource reducer pattern.
 */

export interface Resource<T> {
  state: ResourceState;
  data: UndefinedAble<T>;
  error: UndefinedAble<Error>;
}

export enum ResourceState {
  Idle,
  Loading,
  Success,
  Failure,
}

export type ResourceActionTypes = {
  requestFetching: string;
  fetchSuccess: string;
  fetchFailure: string;
};

export interface FetchResourceAction<T> {
  type: string;
  data: UndefinedAble<T>;
  error: UndefinedAble<Error>;
}

export function fetchResourceDelegate<RootState, Data>(
  actionTypes: ResourceActionTypes,
  reduceRootState: (
    rootState: RootState,
    reduce: (state: Resource<Data>) => Resource<Data>
  ) => RootState
) {
  return {
    [actionTypes.requestFetching]: (
      rootState: RootState,
      action: FetchResourceAction<Data>
    ): RootState =>
      reduceRootState(rootState, (state: Resource<Data>) => {
        return {
          ...state,
          data: undefined,
          state: ResourceState.Loading,
          error: undefined,
        };
      }),
    [actionTypes.fetchSuccess]: (
      rootState: RootState,
      action: FetchResourceAction<Data>
    ): RootState =>
      reduceRootState(rootState, (state: Resource<Data>) => {
        return {
          ...state,
          data: action.data,
          state: ResourceState.Success,
        };
      }),
    [actionTypes.fetchFailure]: (
      rootState: RootState,
      action: FetchResourceAction<Data>
    ): RootState =>
      reduceRootState(rootState, (state: Resource<Data>) => {
        return {
          ...state,
          state: ResourceState.Failure,
          error: action.error,
        };
      }),
  };
}

export const getInitialResourceState = <T>(): Resource<T> => {
  return {
    state: ResourceState.Idle,
    data: undefined,
    error: undefined,
  };
};

export const getLoadingResource = <T>(): Resource<T> => {
  return {
    state: ResourceState.Loading,
    data: undefined,
    error: undefined,
  };
};

export const getSuccessResource = <T>(data: T): Resource<T> => {
  return {
    state: ResourceState.Success,
    data: data,
    error: undefined,
  };
};

export const getFailureResource = <T>(error?: Error): Resource<T> => {
  return {
    state: ResourceState.Failure,
    data: undefined,
    error: error,
  };
};
